import { PawPrint } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { RadioGroup, RadioOption } from '@/customer/components/ui/radio-group'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'

type Species = 'dog' | 'cat'

const SPECIES_LABELS: Record<Species, string> = { dog: 'Dog', cat: 'Cat' }

/**
 * Species row. Read-only once the pet has clinical activity - clinical
 * records key off species, so we don't let members flip it silently.
 * The affordance is dropped when locked; backend rejects PATCH
 * attempts as defence-in-depth.
 */
export function SpeciesPetField({
  pet,
  hasClinicalActivity,
}: {
  pet: PublicPatient
  hasClinicalActivity: boolean
}) {
  const currentSpecies = pet.species as Species
  const updatePet = useUpdatePet(pet.id)
  const speciesField = useEditableField<Species>({
    initialValue: currentSpecies,
    onSave: async ({ value, setError }) => {
      if (value === currentSpecies) return
      try {
        await updatePet.mutateAsync({ species: value })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  return (
    <EditableField
      label="Species"
      icon={PawPrint}
      error={speciesField.error}
      action={hasClinicalActivity ? undefined : <EditButton field={speciesField} label="species" />}
    >
      {speciesField.isEditing ? (
        <RadioGroup
          value={speciesField.draft}
          onValueChange={(v) => speciesField.setDraft(v as Species)}
        >
          <RadioOption value="dog">Dog</RadioOption>
          <RadioOption value="cat">Cat</RadioOption>
        </RadioGroup>
      ) : (
        <EditableValue>{SPECIES_LABELS[currentSpecies] ?? pet.species}</EditableValue>
      )}
      <EditActions field={speciesField} />
    </EditableField>
  )
}
